// **The Match screen's two-colony HUD** (`match-hud`, DESIGN §9's "two-colony state, not a sixth
// screen"). Four pieces, all presentational — every one takes what it draws as props, reads no
// store, and opens no request:
//
//   * [`MatchTimeline`] — the stretched bottom bar and the large `MM:SS` countdown (**D-2**). It is
//     **read-only**: live replay reaches tick *N* by re-simulating, so seek is O(*N*) and is
//     deferred (D-123, DESIGN §15). A strip that looks draggable and is not is worse than a plain
//     one, so there is deliberately no scrub affordance, no `role="slider"` and no pointer handler.
//   * [`ColonySidePanel`] — one player's statistics down one side (**D-3**).
//   * [`StatsGraphModal`] — both players' graphs stacked on one time axis (**D-6**).
//   * [`WinnerBanner`] — the finish, with confetti (**D-8**).
//
// **Colour is never hardcoded** (**D-3**). Every colour arrives as a `var(--colony-N)` token
// resolved by `ui/match/colony-visuals.ts`; the presentation's red/blue is an *override* installed
// over slots 0 and 1 by `app/presentation/usePresentationPalette.ts`. Outside the presentation the
// same components render on the base ramp, which is why nothing here mentions red or blue.

import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import { formatCountdown, formatMatchTime } from './match-clock';
import { CHART_SEASON_BANDS, TimeSeriesChart, type ChartMarker, type ChartSeries } from '../components/chart/TimeSeriesChart';
import { ResourceCounter, COUNTER_PLACEHOLDER } from '../components/hud/ResourceCounter';
import { BeeTypeButton } from '../components/hud/BeeTypeBar';
import { HUD_ICONS } from '../components/hud/hud-icons';
import { abbrevForType, spriteForType } from '../rounds/type-visuals';
import type { StatsSample } from './stats-history';
import type { PromptEntry } from './prompt-feed';
import { launchConfetti } from '../effects/confetti';

// =====================================================================================
// D-2 — the timeline and countdown
// =====================================================================================

export interface MatchTimelineProps {
  /** 0-1 across the summer → autumn → winter split. */
  progress: number;
  /** Straight off the wire (`MatchStateObs.clockRemainingTicks`). */
  remainingTicks: number;
  /** **Wire-required** (`manifest.tickRateHz`) — `null` before Welcome lands, which reads as the
   * placeholder rather than a time invented at 15 Hz (D-082). */
  tickRateHz: number | null;
  /** **Wire-required** (`manifest.speedMultiplier`, WAG-107) — the stream's wall-clock pacing, so
   * the countdown reads the time the player actually spends rather than sim time. `null` before
   * Welcome on the same rule as `tickRateHz`: a placeholder, never an assumed 1x. */
  speedMultiplier: number | null;
}

export function MatchTimeline({
  progress,
  remainingTicks,
  tickRateHz,
  speedMultiplier,
}: MatchTimelineProps) {
  const clamped = Math.max(0, Math.min(1, progress));
  const season =
    CHART_SEASON_BANDS.find((band) => clamped <= band.to) ?? CHART_SEASON_BANDS[CHART_SEASON_BANDS.length - 1];
  return (
    <div
      data-testid="match-timeline"
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 6,
        padding: '10px 18px 14px',
        pointerEvents: 'none',
      }}
    >
      <div
        data-testid="match-countdown"
        style={{
          textAlign: 'center',
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          // Read from the back of a room off a 1920x1080 projection.
          fontSize: 'clamp(40px, 6vw, 96px)',
          lineHeight: 1,
          letterSpacing: '.02em',
          color: 'var(--hud-text, #2B2114)',
          fontVariantNumeric: 'tabular-nums',
          textShadow: '0 2px 10px rgba(0,0,0,.35)',
        }}
      >
        {formatCountdown(remainingTicks, tickRateHz ?? 0, speedMultiplier ?? 0)}
      </div>
      <div
        data-testid="match-timeline-track"
        aria-label={`${season.label}, ${Math.round(clamped * 100)}% elapsed`}
        style={{
          position: 'relative',
          height: 10,
          borderRadius: 5,
          overflow: 'hidden',
          background: 'var(--hud-track)',
          border: '1.5px solid var(--hud-line)',
        }}
      >
        {CHART_SEASON_BANDS.map((band) => (
          <div
            key={band.key}
            data-testid={`timeline-season-${band.key}`}
            style={{
              position: 'absolute',
              left: `${band.from * 100}%`,
              width: `${(band.to - band.from) * 100}%`,
              top: 0,
              bottom: 0,
              background: `var(--season-${band.key})`,
              opacity: 0.55,
            }}
          />
        ))}
        <div
          data-testid="timeline-progress-fill"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${clamped * 100}%`,
            background: 'rgba(43,33,20,.22)',
          }}
        />
        <div
          data-testid="timeline-head"
          style={{
            position: 'absolute',
            left: `${clamped * 100}%`,
            top: -2,
            width: 3,
            height: 14,
            background: '#2B2114',
            borderRadius: 1.5,
          }}
        />
      </div>
    </div>
  );
}

// =====================================================================================
// D-3 — one player's side panel
// =====================================================================================

/** One bee-type chip. `typeId` is `null` for an opponent type, which is known only by name. */
export interface PanelTypeCard {
  typeId: number | null;
  name: string;
  /** `null` while no source exists — rendered as a placeholder, never a fabricated zero. */
  count: number | null;
  /** The type's own colour, resolved by the caller from the colony's roster (**D-3**: this module
   * hardcodes none). Absent falls back to the colony colour, which is what an opponent's
   * name-only card gets before a roster exists for it. */
  colour?: string;
}

export interface ColonySidePanelProps {
  colonyId: number;
  side: 'left' | 'right';
  label: string;
  colour: string;
  bees: number | null;
  nectar: number | null;
  capacity: number | null;
  types: readonly PanelTypeCard[];
  onOpenType: (card: PanelTypeCard) => void;
  onOpenGraph: () => void;
  /** The inline chart summary. */
  graph: ReactNode;
  /** Omitted entirely where the caller may not read this colony's prompts — the section is absent
   * rather than empty, so a player's device never implies there is something withheld. */
  prompts?: readonly PromptEntry[];
  /** **Wire-required** for a prompt's timestamp (`manifest.tickRateHz`): `null` renders the
   * placeholder rather than a time invented at 15 Hz (D-082). */
  tickRateHz?: number | null;
}

/**
 * **The nectar meter** (`spectator-hud-polish` W7).
 *
 * Stored nectar against the colony's own ceiling is a single ratio against a limit, and the form
 * for that is a meter — not the `639/2,040` fraction this used to render, which asks a viewer to
 * divide, and asks it of a room reading a projection from across the room. The fill *is* the
 * ratio; both figures stay on the bar for anyone who wants the numbers.
 *
 * Degrades rather than fabricates: with no capacity there is no ratio to draw, so it falls back to
 * the plain amount, and with no reading at all it shows the same placeholder every other counter
 * here does.
 */
export function NectarMeter({
  colonyId,
  nectar,
  capacity,
}: {
  colonyId: number;
  nectar: number | null;
  capacity: number | null;
}) {
  const known = nectar !== null && Number.isFinite(nectar);
  const ratio =
    known && capacity !== null && Number.isFinite(capacity) && capacity > 0
      ? Math.max(0, Math.min(1, nectar / capacity))
      : null;
  const title =
    known && capacity !== null && Number.isFinite(capacity)
      ? `${Math.round(nectar).toLocaleString()} nectar of capacity ${Math.round(capacity).toLocaleString()}`
      : 'stored nectar';
  return (
    <div
      data-testid={`colony-panel-nectar-${colonyId}`}
      data-fill={ratio === null ? undefined : ratio.toFixed(3)}
      title={title}
      style={{
        position: 'relative',
        flex: 1,
        minWidth: 0,
        height: 34,
        borderRadius: 17,
        overflow: 'hidden',
        background: 'var(--hud-track)',
        border: '1.5px solid var(--hud-line)',
        boxSizing: 'border-box',
      }}
    >
      {ratio !== null && (
        <div
          data-testid={`colony-panel-nectar-fill-${colonyId}`}
          style={{
            width: `${ratio * 100}%`,
            height: '100%',
            background: 'linear-gradient(180deg, var(--honey-300), var(--honey-500))',
          }}
        />
      )}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          padding: '0 9px',
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--hud-text)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <img src={HUD_ICONS.nectar} alt="" style={{ width: 13, height: 13, imageRendering: 'pixelated' }} />
        <span>{known ? Math.round(nectar).toLocaleString() : COUNTER_PLACEHOLDER}</span>
        {capacity !== null && Number.isFinite(capacity) && (
          // The bare ceiling, right-aligned and dim — the word "capacity" moved into the title
          // when the meter joined the bees pill on one row and lost the width for it. The bar
          // itself already says "of a ceiling"; the number says which.
          <span style={{ marginLeft: 'auto', fontWeight: 600, color: 'var(--hud-text-dim)' }}>
            {Math.round(capacity).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
}

const PANEL_LABEL: CSSProperties = {
  fontWeight: 800,
  fontSize: 9,
  textTransform: 'uppercase',
  letterSpacing: '.08em',
  color: 'var(--hud-text-dim)',
};

export function ColonySidePanel({
  colonyId,
  side,
  label,
  colour,
  bees,
  nectar,
  capacity,
  types,
  onOpenType,
  onOpenGraph,
  graph,
  prompts,
  tickRateHz = null,
}: ColonySidePanelProps) {
  return (
    <aside
      data-testid={`colony-panel-${colonyId}`}
      aria-label={`${label} statistics`}
      style={{
        position: 'absolute',
        top: 12,
        bottom: 96,
        [side]: 12,
        width: 268,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        // The banner bleeds to the card's edges, so the horizontal padding moves onto the
        // children rather than sitting on this box.
        padding: '0 0 10px',
        boxSizing: 'border-box',
        background: 'var(--hud-bg)',
        border: '1.5px solid var(--line-strong)',
        borderRadius: 16,
        boxShadow: 'var(--shadow-hud)',
        overflow: 'hidden',
      }}
    >
      {/* **The colony-colour banner.** Identity used to rest on a 12px dot and a 2px border, which
          is a fine badge on a phone and almost nothing across a room — and the border was doing
          double duty as the card's edge. A filled band carries the name at projector distance and
          leaves the border to be a border. Near-white on the colony hue: every `--colony-*` step
          is dark enough to carry it, which is why the palette can be trusted here at all. */}
      <div
        data-testid={`colony-panel-swatch-${colonyId}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '9px 12px 8px',
          background: colour,
          color: 'var(--hud-bg)',
        }}
      >
        <img
          src={HUD_ICONS.worker}
          alt=""
          style={{ width: 18, height: 18, imageRendering: 'pixelated', flex: 'none' }}
        />
        <b
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: 17,
            flex: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </b>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 12px', minHeight: 0, flex: 1 }}>
      {/* 1 — the colony's two headline readings, on ONE row: they are one glance's worth of
          state, and stacking them spent a full row on a bar that is mostly track. Bees keeps the
          kit's `ResourceCounter` with the **worker** icon — the same icon the Colony screen's own
          bees counter wears, and a sprite that still reads as a bee at 18px (the hive tile,
          trialled here first, is a world sprite and collapsed into a grey blob at icon size).
          Nectar is the meter, height-matched to the pill. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div data-testid={`colony-panel-bees-${colonyId}`} style={{ flex: 'none' }}>
          <ResourceCounter icon={HUD_ICONS.worker} value={bees} label="bees" tone="dark" />
        </div>
        <NectarMeter colonyId={colonyId} nectar={nectar} capacity={capacity} />
      </div>

      {/* 2 — bee types, drawn as the **player's own dock chips**. A spectator reading a duel and
          the player reading their own colony now share one vocabulary: the same sprite, the same
          count, the same type-colour underline. The panel used to render name+number boxes with
          no sprite and no type colour, which made the two surfaces look like different games.
          Chips are narrowed to 44px — exactly the kit's `--hit-target` floor — so the five
          presets sit on one row in this 268px panel instead of wrapping at three. */}
      <div>
        <div style={{ ...PANEL_LABEL, marginBottom: 4 }}>Bee types</div>
        <div
          data-testid={`colony-panel-types-${colonyId}`}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            // Tight on purpose: the chip's 44px `--hit-target` floor cannot shrink, so the dock's
            // own chrome is what gives — 5·44 + 4·2 + 2·4 + 3 of border = 239 fits the panel's
            // 242px content and the five presets hold one row. At 3px gap and 5px padding the
            // fifth chip wrapped, verified on the projector.
            gap: 2,
            padding: 4,
            borderRadius: 14,
            border: '1.5px solid var(--line)',
            background: 'var(--surface-card)',
            boxSizing: 'border-box',
          }}
        >
          {types.map((card) => (
            <BeeTypeButton
              key={card.name}
              testId={`panel-type-card-${colonyId}-${card.name}`}
              name={card.name}
              count={card.count}
              color={card.colour ?? colour}
              sprite={spriteForType(card.name)}
              abbrev={abbrevForType(card.name)}
              onClick={() => onOpenType(card)}
              style={{ width: 44 }}
            />
          ))}
        </div>
      </div>

      {/* 3 — the graph, click to expand (D-6). */}
      <button
        type="button"
        data-testid={`colony-graph-open-${colonyId}`}
        onClick={onOpenGraph}
        aria-label={`Expand ${label}'s statistics graph`}
        style={{
          display: 'block',
          width: '100%',
          minHeight: 44,
          padding: 6,
          borderRadius: 10,
          border: '1.5px solid var(--hud-line)',
          background: 'transparent',
          cursor: 'pointer',
        }}
      >
        {graph}
      </button>

      {/* 4 — prompts as **journal entries**, newest first (D-7). Absent where scoping withholds
          them. A colony-colour rule down the left edge attributes the entry without repeating the
          name in a colour the text cannot wear, and the stamp is **match time** rather than a raw
          tick: `tick 2417` is a number from the simulation's clock, not a moment in the match the
          room just watched. */}
      {prompts !== undefined && (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
          <div style={{ ...PANEL_LABEL, marginBottom: 4 }}>Prompts</div>
          <div
            data-testid={`colony-panel-prompts-${colonyId}`}
            style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}
          >
            {prompts.length === 0 ? (
              <span style={{ fontSize: 11, color: 'var(--hud-text-dim)' }}>No prompts yet.</span>
            ) : (
              // Newest first: the reverse of the feed's own oldest-first accretion order.
              [...prompts].reverse().map((prompt) => (
                <article
                  key={prompt.cursor}
                  data-testid={`panel-prompt-${prompt.cursor}`}
                  style={{
                    padding: '6px 9px',
                    borderRadius: '0 9px 9px 0',
                    borderLeft: `3px solid ${colour}`,
                    background: 'var(--surface-sunken)',
                    fontSize: 12,
                    lineHeight: 1.4,
                    color: 'var(--hud-text)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                      fontSize: 9.5,
                      color: 'var(--hud-text-dim)',
                      marginBottom: 2,
                    }}
                  >
                    <span>{prompt.name === '' ? prompt.author : prompt.name}</span>
                    <span>{formatMatchTime(prompt.tick, tickRateHz)}</span>
                  </div>
                  {prompt.text}
                </article>
              ))
            )}
          </div>
        </div>
      )}
      </div>
    </aside>
  );
}

// =====================================================================================
// D-6 — the graph's series, built from the stats history
// =====================================================================================

/** Which statistics are plotted. Data-driven so a later statistic is a table entry (D-6). */
export type GraphSeriesKey = 'types' | 'nectar' | 'capacity';

export const GRAPH_SERIES: readonly { key: GraphSeriesKey; label: string }[] = [
  { key: 'types', label: 'Bees by type' },
  { key: 'nectar', label: 'Nectar' },
  { key: 'capacity', label: 'Capacity' },
];

/**
 * **The stack order for bee-type bands** (`spectator-hud-polish` W8).
 *
 * Not the roster order, and the difference is measured rather than felt: the shipped bee-type
 * palette was run through the dataviz validator against the card surface, and the roster order
 * `harvester · nurse · builder · scout · warrior` fails adjacent-pair CVD separation (builder↔scout
 * ΔE 7.0 protan, inside the 6–8 floor band). This order passes both separation checks — worst
 * adjacent pair ΔE 12.9 deutan, 15.8 normal — so neighbouring bands stay distinguishable without
 * leaning on the direct labels, which are carried anyway because two of these hues sit under 3:1
 * against the surface.
 *
 * Types outside the five presets keep their first-seen order after these, which is deterministic
 * per sample series and therefore stable while the playhead moves.
 */
export const TYPE_STACK_ORDER: readonly string[] = [
  'scout',
  'harvester',
  'nurse',
  'builder',
  'warrior',
];

/** Names in stack order: the presets above first, then anything else in first-seen order. */
export function orderTypeNames(names: readonly string[]): string[] {
  const preset = TYPE_STACK_ORDER.filter((name) => names.includes(name));
  const extra = names.filter((name) => !TYPE_STACK_ORDER.includes(name));
  return [...preset, ...extra];
}

export interface GraphInput {
  samples: readonly StatsSample[];
  colonyId: number;
  colour: string;
  selected: ReadonlySet<GraphSeriesKey>;
  /** Prompt ticks to mark (D-6's vertical lines). */
  promptTicks?: readonly { cursor: number; tick: number }[];
  /**
   * A bee type's own colour, resolved by the caller from the colony's roster.
   *
   * The bands used to be the colony colour at stepped opacity — five shades of one hue, which is
   * not an identity encoding: at projector distance, and for any viewer with a colour-vision
   * deficiency, the bands were one shape. The game already owns a categorical type palette that
   * the dock chips and the panel cards wear, so the graph now speaks the same vocabulary and a
   * band can be matched to a chip by eye. Resolved by the caller for the reason every other colour
   * here is (**D-3**): this module hardcodes none.
   */
  typeColour?: (name: string) => string;
  /** Appended to a band's direct label, after the type name — its count at the latest sample. */
  labelBands?: boolean;
}

export interface GraphModel {
  series: ChartSeries[];
  markers: ChartMarker[];
  totalTicks: number;
  maxValue: number;
}

/**
 * The chart model for one colony. Pure — no React, no store, no fetch — so the stacking arithmetic
 * is unit-testable on its own.
 *
 * The stack is per **bee type**, which is the colony's living **worker** population: the queen is
 * typeless (DESIGN §3) and carries no composition entry, exactly as `populationFromHistogram` and
 * `type-counts.ts` already state. So the top of the stack is `population − 1` for a living colony,
 * and the two readouts agree by construction rather than by coincidence.
 */
export function buildGraphModel({
  samples,
  colonyId,
  colour,
  selected,
  promptTicks = [],
  typeColour,
  labelBands = false,
}: GraphInput): GraphModel {
  const rows = samples
    .map((sample) => ({ tick: sample.tick, row: sample.colonies.find((c) => c.colonyId === colonyId) }))
    .filter((entry): entry is { tick: number; row: NonNullable<typeof entry.row> } => entry.row !== undefined);

  const totalTicks = Math.max(1, ...rows.map((r) => r.tick));
  const series: ChartSeries[] = [];
  let maxValue = 1;

  if (selected.has('types')) {
    const seen: string[] = [];
    for (const { row } of rows) {
      for (const name of Object.keys(row.composition)) {
        if (!seen.includes(name)) seen.push(name);
      }
    }
    const names = orderTypeNames(seen);
    // Running per-tick baseline, so each band sits on the one below it.
    const running = rows.map(() => 0);
    names.forEach((name) => {
      const baseline = [...running];
      const points = rows.map((entry, i) => {
        running[i] += entry.row.composition[name] ?? 0;
        return { tick: entry.tick, value: running[i] };
      });
      const latest = rows.length === 0 ? 0 : (rows[rows.length - 1].row.composition[name] ?? 0);
      series.push({
        id: `type-${name}`,
        label: name,
        // The type's own colour where the caller supplies the roster mapping; the colony colour
        // is the fallback for a caller that has no roster (and matches the old rendering).
        colour: typeColour?.(name) ?? colour,
        kind: 'area',
        points,
        baseline,
        opacity: 1,
        ...(labelBands ? { endLabel: `${name} ${latest.toLocaleString()}` } : {}),
      });
    });
    maxValue = Math.max(maxValue, ...running);
  }

  if (selected.has('nectar')) {
    const points = rows.map((entry) => ({ tick: entry.tick, value: entry.row.storedNectar }));
    series.push({ id: 'nectar', label: 'Nectar', colour, kind: 'line', points, strokeWidth: 2 });
    maxValue = Math.max(maxValue, ...points.map((p) => p.value));
  }

  if (selected.has('capacity')) {
    const points = rows.map((entry) => ({ tick: entry.tick, value: entry.row.capacity }));
    series.push({
      id: 'capacity',
      label: 'Capacity',
      // A **reference**, not a peer series: capacity is the ceiling stored nectar is read
      // against, so it is drawn recessively in ink rather than in the colony's own colour, which
      // previously made two different measures look like two readings of one thing.
      colour: 'var(--hud-text-dim)',
      kind: 'line',
      points,
      strokeWidth: 1.2,
      dashed: true,
      opacity: 0.75,
      ...(labelBands ? { endLabel: 'capacity' } : {}),
    });
    maxValue = Math.max(maxValue, ...points.map((p) => p.value));
  }

  const markers: ChartMarker[] = promptTicks.map((prompt, index) => ({
    id: String(prompt.cursor),
    tick: prompt.tick,
    colour,
    label: String(index + 1),
    title: `Prompt at tick ${prompt.tick}`,
  }));

  return { series, markers, totalTicks, maxValue };
}

// =====================================================================================
// D-6 — the centred modal, both players stacked
// =====================================================================================

export interface GraphSubject {
  colonyId: number;
  label: string;
  colour: string;
  /**
   * **Bees by type**, on its own y scale — a count.
   *
   * Split from `nectar` because one chart cannot honestly carry both (`spectator-hud-polish`
   * W8). A single `model` used to hold the type stack, stored nectar and capacity together, so
   * one axis ran to the largest of three different units: with nectar in the thousands, a
   * ninety-bee colony's whole composition was compressed into the bottom few percent of the plot.
   * Two measures of different scale get two charts — never two y axes, and never one shared.
   */
  composition: GraphModel;
  /** **Stored nectar against capacity**, on its own y scale — an amount. */
  nectar: GraphModel;
  /** Living workers at the latest sample, shown beside the label. `null` where no sample has it. */
  population?: number | null;
}

export interface StatsGraphModalProps {
  subjects: readonly GraphSubject[];
  selected: ReadonlySet<GraphSeriesKey>;
  onToggle: (key: GraphSeriesKey) => void;
  onClose: () => void;
  /** The shared x domain, so both players' charts read at the same scale. */
  totalTicks: number;
  /** **Wire-required** (`manifest.tickRateHz`): the x axis is match time, and `null` renders the
   * placeholder rather than a time invented at 15 Hz (D-082). */
  tickRateHz: number | null;
}

/** The gutters the deep-dive's furniture needs: y labels left, time + season strip below, direct
 * labels right. Stated once so the two charts in a subject share an identical plot origin and
 * their x positions line up down the column. */
const DEEP_DIVE_AXES = { ySteps: 4, padLeft: 36, padTop: 8, padBottom: 26, padRight: 104 } as const;

export function StatsGraphModal({
  subjects,
  selected,
  onToggle,
  onClose,
  totalTicks,
  tickRateHz,
}: StatsGraphModalProps) {
  const formatX = (tick: number): string => formatMatchTime(tick, tickRateHz);
  return (
    <div
      data-testid="stats-graph-modal"
      role="dialog"
      aria-label="Colony statistics"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(20,15,8,.55)',
      }}
    >
      <div
        style={{
          width: 'min(880px, calc(100% - 48px))',
          maxHeight: 'calc(100% - 48px)',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          padding: 18,
          borderRadius: 18,
          background: 'var(--surface-card)',
          border: '1.5px solid var(--line-strong)',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <b style={{ flex: 1, fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 20 }}>
            Colony statistics
          </b>
          <SeriesToggles selected={selected} onToggle={onToggle} />
          <button
            type="button"
            data-testid="stats-graph-close"
            onClick={onClose}
            aria-label="Close the statistics graph"
            style={{
              minWidth: 44,
              minHeight: 44,
              borderRadius: 999,
              border: '1px solid var(--line-strong)',
              background: 'var(--surface-card)',
              color: 'var(--ink-1)',
              fontSize: 16,
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>
        {subjects.map((subject) => (
          <section key={subject.colonyId} data-testid={`stats-graph-subject-${subject.colonyId}`}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 6,
                fontFamily: 'var(--font-display)',
                fontWeight: 600,
                fontSize: 15,
              }}
            >
              <i style={{ width: 12, height: 12, borderRadius: 6, background: subject.colour }} />
              {subject.label}
              {subject.population !== null && subject.population !== undefined && (
                <span
                  data-testid={`stats-graph-total-${subject.colonyId}`}
                  style={{ fontSize: 12, color: 'var(--hud-text-dim)', fontVariantNumeric: 'tabular-nums' }}
                >
                  {subject.population.toLocaleString()} bees
                </span>
              )}
            </div>
            {subject.composition.series.length > 0 && (
              <>
                <ChartHeading>Bees by type</ChartHeading>
                <TimeSeriesChart
                  testId={`stats-graph-chart-${subject.colonyId}`}
                  series={subject.composition.series}
                  markers={subject.composition.markers}
                  totalTicks={totalTicks}
                  maxValue={subject.composition.maxValue}
                  width={820}
                  height={200}
                  axes={{ ...DEEP_DIVE_AXES, formatX }}
                  seasons="strip"
                  markerStyle="axisDot"
                  bandGapPx={2}
                  ariaLabel={`${subject.label} bees by type over the match`}
                />
              </>
            )}
            {subject.nectar.series.length > 0 && (
              <>
                <ChartHeading>Nectar stored</ChartHeading>
                <TimeSeriesChart
                  testId={`stats-graph-nectar-${subject.colonyId}`}
                  series={subject.nectar.series}
                  totalTicks={totalTicks}
                  maxValue={subject.nectar.maxValue}
                  width={820}
                  height={130}
                  axes={{ ...DEEP_DIVE_AXES, ySteps: 2, formatX }}
                  seasons="strip"
                  ariaLabel={`${subject.label} stored nectar over the match`}
                />
              </>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

/** A chart's own caption. Each plot in a subject says what it measures, because the two no longer
 * share an axis and so cannot share a title. */
function ChartHeading({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        ...PANEL_LABEL,
        margin: '10px 0 2px',
      }}
    >
      {children}
    </div>
  );
}

export function SeriesToggles({
  selected,
  onToggle,
}: {
  selected: ReadonlySet<GraphSeriesKey>;
  onToggle: (key: GraphSeriesKey) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 6 }} role="group" aria-label="Plotted statistics">
      {GRAPH_SERIES.map((entry) => {
        const on = selected.has(entry.key);
        return (
          <button
            key={entry.key}
            type="button"
            data-testid={`graph-series-toggle-${entry.key}`}
            aria-pressed={on}
            onClick={() => onToggle(entry.key)}
            style={{
              minHeight: 44,
              padding: '0 12px',
              borderRadius: 999,
              border: '1px solid var(--line-strong)',
              background: on ? 'var(--accent-fill, var(--accent-strong))' : 'transparent',
              color: on ? '#FFFDF6' : 'var(--ink-2)',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {entry.label}
          </button>
        );
      })}
    </div>
  );
}

// =====================================================================================
// D-8 — the winner banner
// =====================================================================================

export interface WinnerBannerProps {
  label: string;
  colour: string;
  /** Injectable so a test drives the burst deterministically; production uses the real thing. */
  fire?: typeof launchConfetti;
}

export function WinnerBanner({ label, colour, fire = launchConfetti }: WinnerBannerProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (host === null) {
      return undefined;
    }
    // `launchConfetti` honours `prefers-reduced-motion` at its own entry point (banner only, no
    // particles), so there is no branch here that could be applied at one call site and forgotten
    // at another. `stop()` retires the particles, cancels the loop and removes the canvas; it is
    // idempotent, so an unmount racing the burst's own self-retirement is safe.
    const handle = fire(host, { colors: [colour, colour, '#FFFDF6'] });
    return () => handle.stop();
  }, [colour, fire]);

  return (
    <div
      ref={hostRef}
      data-testid="match-winner-banner"
      role="status"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          padding: '18px 44px',
          borderRadius: 20,
          background: 'var(--hud-bg)',
          border: `3px solid ${colour}`,
          boxShadow: 'var(--shadow-hud)',
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          fontSize: 'clamp(34px, 5vw, 76px)',
          lineHeight: 1.1,
          color: colour,
          textAlign: 'center',
        }}
      >
        {label} wins
      </div>
    </div>
  );
}

/** A tiny hook the Match screen uses for the modal's toggle set — here so the default series set
 * lives beside the series table it indexes. */
export function useGraphSeriesSelection(): {
  selected: Set<GraphSeriesKey>;
  toggle: (key: GraphSeriesKey) => void;
} {
  const [selected, setSelected] = useState<Set<GraphSeriesKey>>(
    () => new Set<GraphSeriesKey>(['types', 'nectar', 'capacity']),
  );
  const toggle = useMemo(
    () => (key: GraphSeriesKey) => {
      setSelected((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [],
  );
  return { selected, toggle };
}
